import { DEG2RAD, EARTH_RADIUS } from './consts';

export interface Ranked {
  rank: number
}

export type RankedElement<T> = {
  element: T
} & Ranked

export type RankingFunction<T> = (x: T) => number

/**
 * Returns the first parameter passed.
 *
 * @param {T} x anything
 * @returns {T}
 */
export function identity<T>(x: T): T {
  return x
}

/**
 * Ensures the given value is within the min-max range.
 * 
 * @param {number} value the value to constrain
 * @param {number} min allowed minimum value
 * @param {number} max allowed maximum value
 * @returns {number} min if value is smaller than min; max if value is larger than max; passed value otherwise
 */
export function constrain(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, max))
} 

/**
 * Linearly scales the value from its current numeric range to the target range.
 * 
 * @param {number} value value to transform
 * @param {number} valueMin minimum of value's range
 * @param {number} valueMax maximum of value's range
 * @param {number} targetMin target range minimum
 * @param {number} targetMax target range maximum
 * @returns {number} the transform value
 */
export function convertToRange(value: number, valueMin: number, valueMax: number, targetMin: number = 0, targetMax: number = 1): number {
  return (constrain(value, valueMin, valueMax) - valueMin) / (valueMax - valueMin) * (targetMax - targetMin) + targetMin
}

/**
 * Reduces the given array to a sorted number array.
 * 
 * @param {T[]} array 
 * @param {RankingFunction} rankFunc deterministic function that returns a numeric value
 * @returns {RankedElement[]} 
 */
export function rankOrderWithValue<T>(array: ReadonlyArray<T>, rankFunc: RankingFunction<T>): RankedElement<T>[] {
  const distArray = new Array<RankedElement<T>>(array.length)
  for (let i = 0; i < distArray.length; i++) {
    distArray[i] = { rank: rankFunc(array[i]), element: array[i] }
  }

  return distArray.sort((a, b) => a.rank - b.rank)
}

/**
 * Finds the index of the element that is the closest match for the value. It uses binary searching and is thus very fast.
 * 
 * @param {number[]} array array to check against
 * @param {number} value desired value
 * @returns {number} index of the element closest to the searched value
 */
export function determineClosest(array: number[], value: number, maxDistance: number = Infinity): number {
  let indLeft = 0
  let indRight = array.length - 1

  while (indLeft < indRight) {
    const indMedian = Math.floor((indLeft + indRight) / 2)
    if (array[indMedian] === value) {
      return indMedian
    }

    if (array[indMedian] < value) {
      indLeft = indMedian + 1
    } else {
      indRight = indMedian
    }
  }

  if (Math.abs(value - array[indLeft]) <= maxDistance) { // This is the best fit we could find, check to see it its within the limit
    return indLeft
  } else {
    return -1
  }
}

/**
 * Attempts to parse and return the given JSON, if it fails the second paramater is returned.
 * 
 * @param {string} json JSON to desirialize
 * @param {*} defaultValue value to return
 * @returns {*} desirialized JSON value if successful, and defaultValue otherwise
 */
export function tryParseJson(json: string, defaultValue: any = undefined): Object|any {
  try {
    return JSON.parse(json)
  } catch (error) {
    return defaultValue
  }
}

/**
 * Function used to determine whether a string is comprised solely of digits.
 * 
 * @param {string} str the string to check
 * @returns {boolean} true if the string is comprised solely of digits and has a length greater than 0; false otherwsie
 */
export function isDigitsOnly(str: string): boolean {
  return /^[0-9]+$/.test(str)
}

/**
 * Determines whather the passed value is a string or a String instance.
 * 
 * @param {*} val the value to check
 * @returns {boolean} true if the passed value is a sring or String instance; false otherwise
 */
export function isString(val: any): boolean {
  if (typeof val === 'string' || val instanceof String) {
    return true
  }

  return false
}

/**
 * Function used to test whether a string is a valid email address.
 * 
 * @param {string} str the string to check
 * @returns {boolean} true whether the string is a valid email address; false otherwise
 */
export function isEmail(str: string): boolean {
  /* eslint-disable no-control-regex */
  return /^(?:[a-z0-9!#$%&'"*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'"*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/i.test(str)
}

/**
 * Returns the origin string from a URL string.
 * EXAMPLE: getOrigin("https://something.lxnav.com:9999/some/url?someQueryParam=someValue#someAnchor") // => https://something.lxnav.com:9999
 * 
 * @param {string} str url string from whivh to extract the origin 
 * @returns {string|null} returns the origin string if successful, and null oterwise
 */
export function getOrigin(str: string): string|null {
  let url = null
  try {
    url = new URL(str)
  } catch {
    return null
  }

  return url.origin !== 'null' ? url.origin : null
}

/**
 * Removes all whitespace from the beggining and end of a string. Whitespace within the string is replaced with a single space character,
 * EXAMPLE: normalizeWhiteSpace('  aa     bb   ') // 'aa bb'
 * 
 * @param {string} str input string
 * @returns {string} the transformed string
 */
export function normalizeWhiteSpace(str: string): string {
  return str.trim().replace(/\s+/g, ' ')
}

/**
 * Converts a unix timestamp to a Date object.
 * 
 * @param {number} unix unix timestamp
 * @returns {Date} date object representing the timestamp 
 */
export function unixToDate(unix: number): Date {
  return new Date(unix * 1000)
}

/**
 * Converts a date object to a unix timestamp.
 * WARNING: Since Date uses miliseconds internally it's rounded to the closest UNIX time!  
 * 
 * @param {Date} date date to convert
 * @returns {number} unix timestamp
 */
export function dateToUnix(date: Date): number {
  return Math.round(date.getTime() / 1000)
}

/**
 * Function used to paginate an array of items.
 * 
 * @param {array} items items to paginate
 * @param {number} pageIndex index of page
 * @param {number} pageSize size of pages 
 * @returns {array} paginated array of items
 */
export function paginate<T>(items: Array<T>, pageIndex: number, pageSize: number): Array<T> {
  const start = pageIndex * pageSize
  const end = start + pageSize

  return items.slice(start, end)
}
